import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useAnalyticsContext } from '@/components/AnalyticsProvider';
import { EmailService, EmailData } from '@/services/emailService';
import { ReservationService } from '@/services/reservationService';
import { sanitizeFormData } from '@/utils/sanitize';
import { trackEnhancedReservation, trackEnhancedLead } from '@/components/GoogleAdsEnhanced';
import { gtag } from '@/utils/gtag';
import { logger } from '@/utils/logger';
import { useAvailability } from '@/hooks/useAvailability';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

const reservationSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  phone: z.string().min(8, "Le numéro de téléphone doit contenir au moins 8 chiffres"),
  email: z.string().email("Adresse email invalide"),
  dateDebut: z.date({ required_error: "La date de début est requise" }),
  dateFin: z.date({ required_error: "La date de fin est requise" }),
  products: z.object({
    sumo: z.boolean(),
    chateau: z.boolean(),
  }),
  chateauType: z.string().optional(),
  message: z.string().optional(),
}).refine((data) => data.products.sumo || data.products.chateau, {
  message: "Vous devez sélectionner au moins un produit",
  path: ["products"],
}).refine((data) => data.dateFin >= data.dateDebut, {
  message: "La date de fin doit être après la date de début",
  path: ["dateFin"],
});

export type ReservationFormData = z.infer<typeof reservationSchema>;

interface UseReservationFormProps {
  preSelectedChateau?: string;
  chateaux: any[];
  onSuccess?: () => void;
  initialDateDebut?: Date;
  initialDateFin?: Date;
}

export const useReservationForm = ({ 
  preSelectedChateau, 
  chateaux,
  onSuccess,
  initialDateDebut,
  initialDateFin
}: UseReservationFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showChateauType, setShowChateauType] = useState(
    Boolean(preSelectedChateau && preSelectedChateau !== "sumos")
  );
  const [dateDebut, setDateDebut] = useState<Date | undefined>(initialDateDebut);
  const [dateFin, setDateFin] = useState<Date | undefined>(initialDateFin);
  
  const { toast } = useToast();
  const { trackFormInteraction, trackFormSubmission } = useAnalyticsContext();

  // Availability check
  const dateDebutStr = dateDebut ? format(dateDebut, 'yyyy-MM-dd') : undefined;
  const dateFinStr = dateFin ? format(dateFin, 'yyyy-MM-dd') : undefined;
  const { isProductAvailable, isSumoAvailable, hasDatesSelected, loading: availabilityLoading } = useAvailability(dateDebutStr, dateFinStr);

  const form = useForm<ReservationFormData>({
    resolver: zodResolver(reservationSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      dateDebut: undefined,
      dateFin: undefined,
      products: {
        sumo: preSelectedChateau === "sumos",
        chateau: Boolean(preSelectedChateau && preSelectedChateau !== "sumos"),
      },
      chateauType: (preSelectedChateau && preSelectedChateau !== "sumos") ? preSelectedChateau : "",
      message: "",
    },
  });

  // Sync date pickers with form
  useEffect(() => {
    if (dateDebut) form.setValue("dateDebut", dateDebut);
  }, [dateDebut]);

  useEffect(() => {
    if (dateFin) form.setValue("dateFin", dateFin);
  }, [dateFin]);

  // Get available château options filtered by dates
  const getAvailableChateauOptions = () => {
    const allOptions = ReservationService.getChateauOptions(chateaux);
    if (!hasDatesSelected) return allOptions;
    return allOptions.filter(opt => isProductAvailable(opt.id, 'chateau'));
  };

  const handleProductChange = (product: 'sumo' | 'chateau', checked: boolean) => {
    const currentProducts = form.getValues("products");
    form.setValue("products", { ...currentProducts, [product]: checked });
    
    if (product === 'chateau') {
      setShowChateauType(checked);
      if (!checked) {
        form.setValue("chateauType", "");
      }
    }
  };

  const trackConversions = (data: ReservationFormData) => {
    gtag('event', 'conversion', {
      send_to: 'AW-1049756763/u8qKCNm3sIIcENuIyPQD',
      value: 60,
      currency: 'EUR',
      transaction_id: 'res_' + Date.now(),
      email: data.email,
      phone_number: data.phone,
      first_name: data.name?.split(' ')[0],
      last_name: data.name?.split(' ').slice(1).join(' '),
    });

    gtag('event', 'conversion', {
      send_to: 'AW-1049756763/reservation_form_submit',
      value: 60,
      currency: 'EUR',
      event_category: 'reservation',
      event_label: 'website_form'
    });

    gtag('event', 'generate_lead', {
      currency: 'EUR',
      value: 60,
      form_type: 'reservation',
      email: data.email,
      phone_number: data.phone
    });
  };

  const trackGoogleAdsConversions = (data: ReservationFormData) => {
    const estimatedPrice = ReservationService.getEstimatedPrice(data);
    const productType = ReservationService.getProductType(data, chateaux);
    
    trackEnhancedReservation({
      type: productType,
      price: estimatedPrice,
      email: data.email,
      phone: data.phone,
      name: data.name
    });
    
    trackEnhancedLead({
      form_type: 'reservation',
      email: data.email,
      phone: data.phone,
      name: data.name
    });
  };

  const resetForm = () => {
    setTimeout(() => {
      setShowSuccess(false);
      setDateDebut(undefined);
      setDateFin(undefined);
      form.reset({
        name: "",
        phone: "",
        email: "",
        dateDebut: undefined,
        dateFin: undefined,
        products: {
          sumo: false,
          chateau: Boolean(preSelectedChateau),
        },
        chateauType: preSelectedChateau || "",
        message: "",
      });
      setShowChateauType(Boolean(preSelectedChateau));
    }, 3000);
  };

  const onSubmit = async (data: ReservationFormData) => {
    const formLogger = logger.createContext('ReservationForm');
    formLogger.info("Début soumission formulaire");

    // Vérification finale de disponibilité avant insertion
    const wantsChateau = data.products?.chateau;
    const wantsSumo = data.products?.sumo;
    const chateauOk = !wantsChateau || (data.chateauType && isProductAvailable(data.chateauType, 'chateau'));
    const sumoOk = !wantsSumo || isSumoAvailable();

    if (!chateauOk || !sumoOk) {
      toast({
        variant: "destructive",
        title: "Produit indisponible",
        description: "Le produit sélectionné n'est pas disponible pour ces dates. Merci d'en choisir un autre ou de modifier vos dates.",
      });
      return;
    }

    try {
      setIsLoading(true);

      await trackFormSubmission('reservation', data);
      trackConversions(data);


      const sanitizedFormData = sanitizeFormData(data) as ReservationFormData;
      
      // Build product name for reservation
      const productParts: string[] = [];
      if (sanitizedFormData.products?.chateau) {
        const chateauName = sanitizedFormData.chateauType 
          ? chateaux.find(c => c.id === sanitizedFormData.chateauType)?.nom || sanitizedFormData.chateauType
          : 'Château Gonflable';
        productParts.push(chateauName);
      }
      if (sanitizedFormData.products?.sumo) {
        productParts.push('Sumos Gonflables');
      }

      // Save reservation to DB
      const reservationData = {
        client_name: sanitizedFormData.name || '',
        client_email: sanitizedFormData.email || '',
        client_phone: sanitizedFormData.phone || '',
        product_type: sanitizedFormData.products?.chateau ? 'chateau' : 'sumo',
        product_id: sanitizedFormData.chateauType || (sanitizedFormData.products?.sumo ? 'sumo' : null),
        product_name: productParts.join(' + '),
        date_debut: format(data.dateDebut, 'yyyy-MM-dd'),
        date_fin: format(data.dateFin, 'yyyy-MM-dd'),
        status: 'en_attente',
        message: sanitizedFormData.message || null,
      };

      await supabase.from('reservations' as any).insert(reservationData as any);

      // Convert to EmailData format (keep eventDate as string for email compatibility)
      const emailData: EmailData = {
        name: sanitizedFormData.name || '',
        email: sanitizedFormData.email || '',
        phone: sanitizedFormData.phone || '',
        eventDate: format(data.dateDebut, 'yyyy-MM-dd'),
        products: {
          sumo: sanitizedFormData.products?.sumo || false,
          chateau: sanitizedFormData.products?.chateau || false
        },
        chateauType: sanitizedFormData.chateauType || '',
        message: sanitizedFormData.message || ''
      };

      const emailResult = await EmailService.sendReservationEmail(emailData, chateaux);
      await ReservationService.saveToGoogleSheets(emailData, chateaux);

      if (emailResult.success) {
        trackGoogleAdsConversions(sanitizedFormData);
        toast({
          title: "Demande envoyée !",
          description: "Votre demande de réservation a été envoyée avec succès.",
        });
        setShowSuccess(true);
        resetForm();
        onSuccess?.();
        // Redirect to /merci for Google Ads conversion tracking
        setTimeout(() => { window.location.href = '/merci'; }, 2000);
      } else {
        formLogger.error("Tous les systèmes d'email ont échoué");
        toast({
          variant: "destructive", 
          title: "Problème d'envoi d'email",
          description: "Votre demande est sauvegardée. Nous vous contacterons sous 24h.",
        });
        setShowSuccess(true);
        resetForm();
        onSuccess?.();
        setTimeout(() => { window.location.href = '/merci'; }, 2000);
      }
    } catch (error: any) {
      formLogger.error("Erreur critique", error);
      toast({
        title: "Erreur technique",
        description: "Veuillez nous contacter directement par téléphone.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    showSuccess,
    showChateauType,
    handleProductChange,
    onSubmit,
    chateauOptions: getAvailableChateauOptions(),
    dateDebut,
    dateFin,
    setDateDebut,
    setDateFin,
    hasDatesSelected,
    availabilityLoading,
    isProductAvailable,
    isSumoAvailable,
  };
};
